import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useUnitProvider } from '/@/core/context/unit';
import { useMutateCreateOrUpdateCropMonitoring } from '/@/hooks/crop-monitoring/use-crop-monitoring';
import { useFieldById } from '/@/hooks/fields';
import { plantedAreaForForm } from '/@/hooks/geometry/area';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import type { CropMonitoring } from '/@/types/crop-monitoring';
import NewCropMonitoringFormModal from '../../../../creation-dialog/components/NewCropMonitoringFormModal';
import ExitWithoutSavingModal from '../../../../creation-dialog/ExitWithoutSavingModal';
import type { CropMonitoringFormType } from '../../../../creation-dialog/types';
import type { RegistryAddStep } from './types';

const CreateRegistryPlanting = ({
    open,
    onClose,
    onSuccess,
    setStep,
    fieldId,
    cropMonitoring: cropMonitoringProp,
    initialValues,
}: {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    setStep?: (step: RegistryAddStep) => void;
    fieldId?: number;
    cropMonitoring?: CropMonitoring;
    /** Prefill for the "Registrar lo aplicado" flow — seeds the form from a recommendation. */
    initialValues?: Partial<CropMonitoringFormType>;
}) => {
    const fieldQuery = useFieldById({ id: fieldId ?? 0 }, { enabled: !!fieldId });
    const field = fieldQuery.data;

    let cropMonitoring = cropMonitoringProp ?? field?.activeCropMonitoring;
    if (cropMonitoring?.harvestDate) {
        cropMonitoring = undefined;
    }

    const [showExitWarning, setShowExitWarning] = useState<boolean>(false);
    const units = useUnitProvider();

    // This registry flow tracks a single planting, but the shared modal and HybridBlock
    // read/write `hybrids`, so the form must use that structure. The legacy flat prefill
    // (`initialValues` from the recommendation flow) is mapped into the single hybrid.
    const initialHybrid = {
        id: cropMonitoring?.id,
        plantingDate: initialValues?.plantingDate ?? cropMonitoring?.plantingDate,
        product: initialValues?.product ?? cropMonitoring?.cornCropMonitoring?.product ?? cropMonitoring?.productName,
        technology: cropMonitoring?.cornCropMonitoring?.technology,
        environment: initialValues?.environment ?? cropMonitoring?.environment,
        plantedArea: plantedAreaForForm(units, {
            reportHa: cropMonitoring?.plantedArea,
            fieldM2: field?.area,
        }),
        density: initialValues?.density,
    };

    const form = useForm<CropMonitoringFormType>({
        defaultValues: {
            fieldId: initialValues?.fieldId ?? cropMonitoring?.field ?? field?.id,
            id: cropMonitoring?.id,
            crop: initialValues?.crop ?? cropMonitoring?.crop ?? '',
            previousCrop: cropMonitoring?.previousCrop || '',
            hybrids: [initialHybrid],
        },
    });

    useEffect(() => {
        if (!open) return;
        mpTrackEvent('Add Crop Flow create began', { entry_source: 'field-detail' });
    }, [open]);

    const { fieldId: formFieldId, crop, previousCrop, hybrids } = form.watch();

    const { trigger: createOrUpdateCropMonitoring, isMutating } = useMutateCreateOrUpdateCropMonitoring(
        {
            hybrids,
            field: field?.id || formFieldId || 0,
            crop: crop || undefined,
            previousCrop: previousCrop === 'none' ? undefined : previousCrop || undefined,
            id: cropMonitoring?.id,
        },
        {
            onSuccess: () => {
                form.reset();
                onSuccess();
            },
        }
    );

    const handleClose = () => {
        if (isMutating) return;
        const firstHybrid = hybrids?.[0];
        const hasData =
            crop ||
            previousCrop ||
            !!(
                firstHybrid?.plantingDate ||
                firstHybrid?.environment ||
                firstHybrid?.plantedArea ||
                firstHybrid?.density ||
                firstHybrid?.product ||
                firstHybrid?.technology
            );
        if (hasData) {
            setShowExitWarning(true);
        } else {
            onClose();
            form.reset();
        }
    };

    const handleDiscardAndExit = () => {
        setShowExitWarning(false);
        onClose();
        form.reset();
    };

    if (!open) return null;

    return (
        <>
            <NewCropMonitoringFormModal
                open={open && !showExitWarning}
                setClose={handleClose}
                goBack={setStep ? () => setStep('select') : undefined}
                form={form}
                field={field}
                onSubmit={() => createOrUpdateCropMonitoring()}
                submitting={isMutating}
                onlyEdit={false}
                entry_source='field-detail'
            />
            <ExitWithoutSavingModal
                open={showExitWarning}
                setClose={() => setShowExitWarning(false)}
                onDiscard={handleDiscardAndExit}
            />
        </>
    );
};

export default CreateRegistryPlanting;
